"use client";

import Link from 'next/link';
import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';

import { apiClient } from '../lib/api-client';
import type { CaseSearchResult } from '../lib/types';

export default function PortalPage() {
  const router = useRouter();
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<CaseSearchResult[]>([]);
  const [searching, setSearching] = useState(false);

  const [newConstructionName, setNewConstructionName] = useState('');
  const [newConstructionNameKana, setNewConstructionNameKana] = useState('');
  const [newMeetingDate, setNewMeetingDate] = useState('');
  const [newSiteAddress, setNewSiteAddress] = useState('');
  const [creating, setCreating] = useState(false);
  const [importProjectId, setImportProjectId] = useState<string | null>(null);

  const [legacyQuery, setLegacyQuery] = useState('');
  const [legacyResults, setLegacyResults] = useState<CaseSearchResult[]>([]);
  const [legacyLoading, setLegacyLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const q = query.trim();

    if (!q) {
      setSuggestions([]);
      setSearching(false);
      return () => {
        cancelled = true;
      };
    }

    const t = window.setTimeout(() => {
      (async () => {
        setSearching(true);
        try {
          const results = await apiClient.cases.search(q, 20);
          if (!cancelled) setSuggestions(results);
        } catch (error) {
          console.error('Failed to search cases:', error);
          if (!cancelled) setSuggestions([]);
        } finally {
          if (!cancelled) setSearching(false);
        }
      })();
    }, 200);

    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [query]);

  useEffect(() => {
    let cancelled = false;
    const q = legacyQuery.trim();

    const t = window.setTimeout(() => {
      (async () => {
        setLegacyLoading(true);
        try {
          const results = await apiClient.legacy.contracts(q, 20);
          if (!cancelled) setLegacyResults(results);
        } catch (error) {
          console.error('Failed to fetch legacy contracts:', error);
          if (!cancelled) setLegacyResults([]);
        } finally {
          if (!cancelled) setLegacyLoading(false);
        }
      })();
    }, 200);

    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [legacyQuery]);

  const handleImport = (c: CaseSearchResult) => {
    setNewConstructionName(c.constructionName);
    setNewConstructionNameKana(c.constructionNameKana ?? '');
    setNewSiteAddress(c.siteAddress ?? '');
    setImportProjectId(c.projectId);
  };

  const canCreate = newConstructionName.trim() && newMeetingDate.trim();
  const hasQuery = query.trim().length > 0;

  const createNew = async () => {
    if (!canCreate) return;
    setCreating(true);
    try {
      const c = await apiClient.cases.create({
        projectId: importProjectId?.trim() || undefined,
        constructionName: newConstructionName.trim(),
        constructionNameKana: newConstructionNameKana.trim() || undefined,
        siteAddress: newSiteAddress.trim() || undefined,
      });
      const m = await apiClient.meetings.create({
        projectId: c.projectId,
        meetingDate: newMeetingDate,
        headerSnapshot: {
          constructionName: c.constructionName,
          siteAddress: c.siteAddress,
        },
        data: {},
      });
      router.push(`/meeting/${encodeURIComponent(m.meetingId)}`);
      setImportProjectId(null);
    } catch (error) {
      console.error('Failed to create case/meeting:', error);
      alert('作成に失敗しました。もう一度お試しください。');
    } finally {
      setCreating(false);
    }
  };

  return (
    <main className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-5xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">色決めリスト ポータル</h1>
              <p className="text-gray-600 mt-1">案件の検索・新規作成・再開</p>
            </div>
            <div className="flex gap-3">
              <Link
                href="/editor"
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                既存入力画面へ
              </Link>
              <button
                onClick={() => window.open(process.env.NEXT_PUBLIC_NEXUS_URL ?? 'https://nexus.answpro.com', '_blank')}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                総合窓口へ
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 py-8">
        <section className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b bg-gradient-to-r from-blue-50 to-indigo-50">
            <h2 className="text-xl font-semibold text-gray-800">保存済み案件一覧</h2>
            <p className="text-sm text-gray-600 mt-1">このアプリで作成済みの案件を検索してすぐに開けます</p>
          </div>
          <div className="p-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">保存済み案件を検索</label>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="工事名・かな・建築地・projectId で部分一致"
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-gray-900 placeholder:text-gray-500 placeholder:opacity-100 disabled:placeholder:text-gray-500 disabled:text-gray-500 disabled:bg-gray-100"
            />

            {hasQuery && searching && <div className="mt-3 text-sm text-gray-600">検索中...</div>}

            {hasQuery ? (
              <div className="mt-4 border rounded-lg overflow-hidden divide-y">
                {suggestions.map((c) => (
                  <div key={c.projectId} className="px-4 py-3 flex flex-col gap-2 bg-white hover:bg-gray-50 transition-colors">
                    <div className="flex flex-wrap items-center gap-4 justify-between">
                      <div>
                        <div className="font-medium text-gray-900">{c.constructionName}</div>
                        {c.constructionNameKana && (
                          <div className="text-xs text-gray-500">{c.constructionNameKana}</div>
                        )}
                        {c.siteAddress && <div className="text-xs text-gray-500">{c.siteAddress}</div>}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Link
                          href={`/case/${encodeURIComponent(c.projectId)}`}
                          className="px-3 py-1.5 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors"
                        >
                          案件ページへ
                        </Link>
                        <Link
                          href={`/editor?projectId=${encodeURIComponent(c.projectId)}`}
                          className="px-3 py-1.5 text-sm rounded-lg bg-gray-900 text-white hover:bg-black transition-colors"
                        >
                          /editor を開く
                        </Link>
                      </div>
                    </div>
                    {c.meetingDates.length > 0 ? (
                      <div className="text-xs text-gray-500">打合せ: {c.meetingDates.join(', ')}</div>
                    ) : (
                      <div className="text-xs text-gray-400">打合せがまだ登録されていません</div>
                    )}
                  </div>
                ))}
                {suggestions.length === 0 && !searching && (
                  <div className="px-4 py-6 text-sm text-gray-600 text-center">候補がありません</div>
                )}
              </div>
            ) : (
              <div className="mt-4 text-sm text-gray-400 text-center">検索ワードを入力すると保存済み案件が表示されます</div>
            )}
          </div>
        </section>

        <section className="bg-white rounded-xl shadow-sm overflow-hidden mt-8">
          <div className="px-6 py-4 border-b bg-gradient-to-r from-amber-50 to-orange-50">
            <h2 className="text-xl font-semibold text-gray-800">契約済み案件（未取り込み）</h2>
            <p className="text-sm text-gray-600 mt-1">AnswerERP から契約ステータスの案件を取得し、まだ取り込んでいないものを表示します</p>
          </div>
          <div className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">顧客名・かな・建築地などで絞り込み</label>
              <input
                type="text"
                value={legacyQuery}
                onChange={(e) => setLegacyQuery(e.target.value)}
                placeholder="くろさわ / 黒沢 / 黒沢様邸 など"
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-gray-900 placeholder:text-gray-500 placeholder:opacity-100"
              />
            </div>

            {legacyLoading && <div className="text-sm text-gray-600">契約案件を取得中...</div>}

            <div className="border rounded-lg overflow-hidden divide-y">
              {legacyResults.map((c) => (
                <div key={`legacy-${c.projectId}`} className="px-4 py-3 flex flex-col gap-1 bg-white hover:bg-gray-50 transition-colors">
                  <div className="flex flex-wrap items-center gap-3 justify-between">
                    <div>
                      <div className="font-medium text-gray-900">{c.constructionName}</div>
                      {c.constructionNameKana && (
                        <div className="text-xs text-gray-500">{c.constructionNameKana}</div>
                      )}
                      {c.siteAddress && <div className="text-xs text-gray-500">{c.siteAddress}</div>}
                      <div className="text-xs text-gray-400">projectId: {c.projectId}</div>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleImport(c)}
                      className="px-3 py-1.5 text-sm rounded-lg bg-orange-500 text-white hover:bg-orange-600 transition-colors"
                    >
                      取り込む
                    </button>
                  </div>
                  <div className="text-xs text-gray-500">契約済み・未取り込み</div>
                </div>
              ))}
              {legacyResults.length === 0 && !legacyLoading && (
                <div className="px-4 py-6 text-sm text-gray-500 text-center">表示する契約案件がありません</div>
              )}
            </div>
          </div>
        </section>

        <section className="bg-white rounded-xl shadow-sm overflow-hidden mt-8">
          <div className="px-6 py-4 border-b bg-gradient-to-r from-blue-50 to-indigo-50">
            <h2 className="text-xl font-semibold text-gray-800">新規作成</h2>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">工事名（=お客様名）</label>
                <input
                  type="text"
                  value={newConstructionName}
                  onChange={(e) => setNewConstructionName(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-gray-800 placeholder:text-gray-800 placeholder:opacity-100 disabled:text-gray-700 disabled:placeholder:text-gray-700 disabled:bg-gray-100 disabled:opacity-100"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">打合せ日</label>
                <input
                  type="date"
                  value={newMeetingDate}
                  onChange={(e) => setNewMeetingDate(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-gray-800 placeholder:text-gray-800 placeholder:opacity-100 disabled:text-gray-700 disabled:placeholder:text-gray-700 disabled:bg-gray-100 disabled:opacity-100"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">建築地</label>
                <input
                  type="text"
                  value={newSiteAddress}
                  onChange={(e) => setNewSiteAddress(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-gray-800 placeholder:text-gray-800 placeholder:opacity-100 disabled:text-gray-700 disabled:placeholder:text-gray-700 disabled:bg-gray-100 disabled:opacity-100"
                  placeholder="任意"
                />
              </div>
            </div>
 
            <div className="mt-6">
              <button
                type="button"
                onClick={createNew}
                disabled={!canCreate || creating}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  !canCreate || creating
                    ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                {creating ? '作成中...' : '作成して編集へ'}
              </button>
              {!canCreate && (
                <div className="mt-2 text-xs text-gray-600">工事名と打合せ日を入力してください</div>
              )}
            </div>
          </div>
        </section>
      </div>
    </main>
  );
}

